import {Delete} from '@mui/icons-material';
import {useCanAny, useCan} from 'src/permissions/hooks';
import {IconButton} from '@mui/material';
import {FC, useState} from 'react';
import {useIntl} from 'react-intl';
import {companyApi, employeeApi, usersApi} from 'src/api';
import {useAppDispatch} from 'src/app/hooks';
import AMPModal from 'src/components/common/AMPModal';
import {ConformationModal} from 'src/components/common/ConformationModal';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {AppDispatch, BaseUserInfo} from 'src/types';
import {NotifyError} from 'src/utils/notification';
import {UserTypeEnum} from 'src/enums';
import theme from 'src/theme';

const handleDeleteUserByRole =
    (
        userTrigger: ReturnType<typeof usersApi.useDeleteUserMutation>[0],
        companyTrigger: ReturnType<typeof companyApi.useDeleteCompanyMutation>[0],
        employeeTrigger: ReturnType<typeof employeeApi.useDeleteEmployeeByIdMutation>[0],
        dispatch: AppDispatch,
        row: BaseUserInfo,
        handleClose: () => void,
        errorMessage: string
    ) =>
        async () => {
            try {
                dispatch(preloaderChangeStatus(true));

                // Determine user role
                // The row's own classification, not a role name: which endpoint deletes this user is a
                // property of what they are, and userType keeps saying so under custom role names.
                const userType = row.userType;

                if (userType === UserTypeEnum.EMPLOYEE && row.employee?.id && row.employee?.company?.id) {
                    // Delete employee using employee endpoint
                    await employeeTrigger({
                        employeeId: row.employee.id,
                        params: {
                            companyId: row.employee.company.id,
                            businessId: +row.business.id,
                        },
                    }).unwrap();
                } else if (userType === UserTypeEnum.COMPANY && row.company?.id) {
                    // Delete a company using company endpoint
                    await companyTrigger({
                        companyId: row.company.id,
                        businessId: +row.business.id,
                    }).unwrap();
                } else {
                    // Delete regular user using users endpoint
                    await userTrigger({
                        userId: +row.id,
                        businessId: +row.business.id,
                    }).unwrap();
                }


            } catch (error: any) {
                error.status === 400 && NotifyError(errorMessage);
            } finally {
                // Manually invalidate users' cache for company and employee deletions
                // The row's own classification, not a role name: which endpoint deletes this user is a
                // property of what they are, and userType keeps saying so under custom role names.
                const userType = row.userType;
                if (userType === UserTypeEnum.COMPANY || userType === UserTypeEnum.EMPLOYEE) {
                    dispatch(usersApi.util.invalidateTags([{type: 'User', id: 'LIST'}]));
                }

                dispatch(preloaderChangeStatus(false));
                handleClose();
            }
        };

export const DeleteUser: FC<{ row: BaseUserInfo }> = ({row}) => {
    // Three deletions behind one control - the row decides which by user type - so the button is
    // worth showing if any of them is permitted, and the server decides the rest.
    const canDelete = useCanAny('users.deleteAccount', 'employees.delete', 'companies.delete');
    const [userTrigger] = usersApi.useDeleteUserMutation();
    const [companyTrigger] = companyApi.useDeleteCompanyMutation();
    const [employeeTrigger] = employeeApi.useDeleteEmployeeByIdMutation();
    const dispatch = useAppDispatch();

    const intl = useIntl();
    const [open, setOpen] = useState<boolean>(false);
    const dialogTitle = intl.formatMessage({id: 'deleteUser'});

    const handleOpenModal = () => {
        setOpen(true);
    };

    const handleCloseModal = () => {
        setOpen(false);
    };

    if (!canDelete) {
        return null;
    }

    return (
        <>
            <IconButton
                sx={{
                    width: '28px',
                    height: '28px',
                    p: '2px',
                    color: theme.palette.neutral[400],
                    backgroundColor: 'transparent',
                    '.MuiSvgIcon-root': {
                        fill: theme.palette.neutral[400],
                        width: '20px',
                    },
                }}
                onClick={handleOpenModal}
            >
                <Delete/>
            </IconButton>
            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <ConformationModal
                    handleClose={handleCloseModal}
                    handleSubmit={handleDeleteUserByRole(
                        userTrigger,
                        companyTrigger,
                        employeeTrigger,
                        dispatch,
                        row,
                        handleCloseModal,
                        intl.formatMessage({id: 'cannotDeleteUser'})
                    )}
                    text={'deletionUser'}
                    caption={`${row.firstName} ${row.lastName}`}
                />
            </AMPModal>
        </>
    );
};
